import { StyleSheet, View } from "react-native";
import { Button, TextInput, HelperText } from "react-native-paper";
import React, { useState } from "react";
import { NativeStackScreenProps } from "@react-navigation/native-stack";
import { useTranslation } from "react-i18next";
import { signUp } from "../../services/authService";
import {
  checkInvitation,
  updateInvitation,
} from "../../services/employeeServices";
import { useForm, Controller } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { DatePickerInput } from "react-native-paper-dates";

type Props = NativeStackScreenProps<any>;

const registerSchema = z
  .object({
    firstName: z.string().min(2, "First name must be at least 2 characters"),
    lastName: z.string().min(2, "Last name must be at least 2 characters"),
    email: z.email({ message: "Invalid email address" }),
    password: z.string().min(6, "Password must be at least 6 characters"),
    confirmPassword: z.string(),
    birthDate: z.date().optional(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords don't match",
    path: ["confirmPassword"],
  });

type RegisterFormData = z.infer<typeof registerSchema>;

export default function RegisterScreen({ navigation }: Props) {
  const { t } = useTranslation();
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [loading, setLoading] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      password: "",
      confirmPassword: "",
      birthDate: undefined,
    },
  });

  const onSubmit = async (data: RegisterFormData) => {
    try {
      setLoading(true);
      const invitation = await checkInvitation(data.email);
      if (invitation) {
        await updateInvitation(invitation.id);
      }
      await signUp({
        email: data.email,
        password: data.password,
        firstName: data.firstName,
        lastName: data.lastName,
        role: invitation?.role || "",
        birthDate: data.birthDate?.toISOString().split("T")[0],
      });
      setLoading(false);
    } catch (error) {
      alert(t((error as { code: string }).code));
      setLoading(false);
    }
  };

  return (
    <View style={styles.container}>
      <View style={styles.formContainer}>
        <Controller
          control={control}
          name="firstName"
          render={({ field: { onChange, value } }) => (
            <>
              <TextInput
                mode="outlined"
                value={value}
                onChangeText={onChange}
                label={t("First Name")}
                error={!!errors.firstName}
                id="firstName"
              />
              <HelperText
                type="error"
                visible={!!errors.firstName}
                style={styles.helperText}
              >
                {errors.firstName?.message}
              </HelperText>
            </>
          )}
        />

        <Controller
          control={control}
          name="lastName"
          render={({ field: { onChange, value } }) => (
            <>
              <TextInput
                mode="outlined"
                value={value}
                onChangeText={onChange}
                label={t("Last Name")}
                error={!!errors.lastName}
                id="lastName"
              />
              <HelperText
                type="error"
                visible={!!errors.lastName}
                style={styles.helperText}
              >
                {errors.lastName?.message}
              </HelperText>
            </>
          )}
        />

        <Controller
          control={control}
          name="email"
          render={({ field: { onChange, value } }) => (
            <>
              <TextInput
                keyboardType="email-address"
                testID="email-sign-up"
                mode="outlined"
                autoCapitalize="none"
                value={value}
                onChangeText={onChange}
                label="Email"
                error={!!errors.email}
                id="email"
              />
              <HelperText
                type="error"
                visible={!!errors.email}
                style={styles.helperText}
              >
                {errors.email?.message}
              </HelperText>
            </>
          )}
        />

        <Controller
          control={control}
          name="birthDate"
          render={({ field: { onChange, value } }) => (
            <DatePickerInput
              mode="outlined"
              locale="en"
              label={t("Birthday")}
              value={value}
              onChange={onChange}
              inputMode="start"
            />
          )}
        />

        <Controller
          control={control}
          name="password"
          render={({ field: { onChange, value } }) => (
            <>
              <TextInput
                testID="password-sign-up"
                mode="outlined"
                secureTextEntry={!passwordVisible && !__DEV__}
                value={value}
                onChangeText={onChange}
                label={t("Password")}
                error={!!errors.password}
                right={
                  <TextInput.Icon
                    onPress={() => setPasswordVisible(!passwordVisible)}
                    icon={passwordVisible ? "eye" : "eye-off"}
                  />
                }
                id="password"
              />
              <HelperText
                type="error"
                visible={!!errors.password}
                style={styles.helperText}
              >
                {errors.password?.message}
              </HelperText>
            </>
          )}
        />

        <Controller
          control={control}
          name="confirmPassword"
          render={({ field: { onChange, value } }) => (
            <>
              <TextInput
                testID="confirm-password-sign-up"
                mode="outlined"
                secureTextEntry={!passwordVisible && !__DEV__}
                value={value}
                onChangeText={onChange}
                label={t("Confirm Password")}
                error={!!errors.confirmPassword}
                right={
                  <TextInput.Icon
                    onPress={() => setPasswordVisible(!passwordVisible)}
                    icon={passwordVisible ? "eye" : "eye-off"}
                  />
                }
                id="confirm-password"
              />
              <HelperText type="error" visible={!!errors.confirmPassword}>
                {errors.confirmPassword?.message}
              </HelperText>
            </>
          )}
        />

        <Button
          style={styles.button}
          disabled={loading}
          onPress={() => navigation.goBack()}
        >
          {t("Have an account?")} {t("Sign in")}
        </Button>
        <Button
          testID="sign-up"
          mode="contained"
          loading={loading}
          disabled={loading}
          onPress={handleSubmit(onSubmit)}
          style={styles.button}
        >
          {t("Sign up")}
        </Button>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    alignItems: "center",
    padding: 16,
  },
  formContainer: {
    maxWidth: 400,
    width: "100%",
    gap: 10,
  },
  button: {
    marginTop: 8,
  },
  helperText: {
    marginVertical: -4,
  },
});
